// 36 calendar SECTIONS — three per month, the standard climatological "dekads":
// days 1–10, 11–20, 21–end. One frame = the beginning / middle / end of a month.
//
// This is deliberately coarser than a daily normal: grouping ~10 days together
// signals honestly that the map is a typical-climate ESTIMATE, not a day-precise
// forecast. 12 months × 3 parts = 36 frames.
//
// Keys are "MM-p" (p = 1|2|3). The leading zero-padded month means the key still
// parses as the month everywhere an old "MM-DD" key did (slice(0,2)), and a plain
// lexical sort keeps calendar order ("01-1" < "01-2" < … < "12-3").
//
// Pure ES module, no dependencies, so both the Node pipeline (merge.ts) and the
// browser frontend (app.ts) import it.

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
// Dekad 1 / 2 / 3 -> how it reads to a normal person.
export const PART_LABELS = ['Early', 'Mid', 'Late'];

// Which dekad a day-of-month falls in.
export type Part = 1 | 2 | 3;

export function dekadOfDay(d: number): Part {
  return d <= 10 ? 1 : d <= 20 ? 2 : 3;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

// "MM-DD" -> section key "MM-p".
export function sectionKeyForMmdd(md: string): string {
  const [m, d] = md.split('-').map(Number);
  return `${pad2(m)}-${dekadOfDay(d)}`;
}

// { month: 1..12, part: 1..3 } from a section key.
export function parseSection(key: string): { month: number; part: number } {
  const [m, p] = key.split('-').map(Number);
  return { month: m, part: p };
}

// The 36 section keys in calendar order.
export const SECTION_KEYS: string[] = [];
for (let m = 1; m <= 12; m++) {
  for (let p = 1; p <= 3; p++) SECTION_KEYS.push(`${pad2(m)}-${p}`);
}

// "Early July" — the human label for a section key.
export function sectionLabel(key: string): string {
  const { month, part } = parseSection(key);
  return `${PART_LABELS[part - 1]} ${MONTH_NAMES[month - 1]}`;
}

// "Early Jul" — short month, for compact record/tick labels.
export function sectionLabelShort(key: string): string {
  const { month, part } = parseSection(key);
  return `${PART_LABELS[part - 1]} ${MONTH_NAMES[month - 1].slice(0, 3)}`;
}

// Calendar-day span of a section: dekads 1 and 2 are always 10 days; the third
// runs day 21 to month-end, so its length depends on the month (Feb counted as 28,
// the typical non-leap year). Used to turn a mean rain RATE into a dekad's
// cumulative rainfall.
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export function daysInSection(key: string): number {
  const { month, part } = parseSection(key);
  return part < 3 ? 10 : DAYS_IN_MONTH[month - 1] - 20;
}
